<template>
  <div class="dashboard f-h">
    <div class="dashboard__content">
      <!-- Left section: indicators and actions -->
      <div class="dashboard__left">
        <div v-if="nextEvents.length > 0 && isLandscape" class="dashboard__right__next-events generic-border">
          <div class="legend legend--bottom-margin">
            {{ $t('message.UPCOMING_EVENTS') }}
          </div>
          <UpcomingEvent v-for="event in nextEvents" :key="event.id" :event="event" />
        </div>
        <!-- Indicator section -->
        <div class="dashboard__left__indicators">
          <Indicators
            ref="indicators"
            :custom-themes-margin="0.5"
            :custom-indic-margin="0.5"
            :max-indicators-in-col="3"
            :min-indicators-in-col="1"
            as-component
          />
        </div>
        <!-- END Indicator section -->
        <!-- Action section -->
        <div class="dashboard__left__actions generic-border">
          <Actions
            ref="actions"
            landscape
            prevent-dragging-actions
            as-component
          />
        </div>
        <!-- END Action section -->
      </div>
      <!-- END Left section: indicators and actions -->

      <!-- Right section: problems and info -->
      <div class="dashboard__right" :class="rightSectionClasses">
        <div v-if="nextEvents.length > 0 && !isLandscape" class="dashboard__right__next-events generic-border">
          <div class="legend legend--bottom-margin">
            {{ $t('message.UPCOMING_EVENTS') }}
          </div>
          <UpcomingEvent v-for="event in nextEvents" :key="event.id" :event="event" />
        </div>
        <div class="dashboard__right__pb-infos">
          <div
            v-if="displayInformations"
            class="dashboard__right__infos generic-border"
            :style="rightSectionsHeight"
          >
            <MeetingInfos ref="infos" as-component no-buttons-on-items />
          </div>
          <div class="dashboard__right__problems generic-border" :style="rightSectionsHeight">
            <Problems ref="problems" as-component no-menu />
          </div>
        </div>
      </div>
      <!-- END Right section: problems and info -->
    </div>
  </div>
</template>

<script>
/**
 * Dashboard page. Default home page of Shizen.
 * Features  4 default views : Problems, indicators, actions and information.
 */
import $ from 'jquery';
import cloneDeep from 'lodash/cloneDeep';
import { mapState } from 'vuex';
// Import components
import Actions from '@/views/dmp/actions.vue';
import Indicators from '@/views/dmp/indicators.vue';
import MeetingInfos from '@/views/dmp/meeting-infos.vue';
import Problems from '@/views/dmp/problems.vue';
import UpcomingEvent from '@/components/upcoming-event.vue';
// Import socket handling utils
import mixinDashboardOptions from '@/mixins/page-options/dashboard.vue';
// Vuex utils
import {
  FETCH_TEAMS,
  FETCH_NEXT_MEETINGS,
  FETCH_NEXT_GWS,
  FETCH_USERS,
} from '@/store/action-types';
import {
  GET_NEXT_MEETINGS,
  GET_NEXT_GWS,
  GET_USERS,
} from '@/store/mutation-types';
// Import utils
import IAHelpStep from '@/utils/classes/IAHelpStep';
import IAHelpStepsGroup from '@/utils/classes/IAHelpStepsGroup';


export default {

  name: 'Dashboard',

  components: {
    Actions,
    Indicators,
    MeetingInfos,
    Problems,
    UpcomingEvent,
  },

  mixins: [mixinDashboardOptions],

  data() {
    return {
      /** Next meting */
      meetingsRefreshTask: null,
      nextMeetingKeyModifier: 1,
      minIndicatorsInColumn: 3,
      // Properties used by other components. See src/views/readme.md
      pageTitle: this.$t('message.DASHBOARD'),
      iaInitialBtns: [],
      iaAnalysisText: this.$t('message.IA_ANALYSIS_TEXT'),
      iaTeachText: this.$t('sensei.TEACHER_TEXT'),
      iaTeachBtns: [this.teacherBtn('INDICATORS'), this.teacherBtn('PLAN_ACTION'), this.teacherBtn('SPOT_PB')],
    };
  },

  computed: {
    /** Object: Classes of the right section */
    rightSectionClasses() {
      return {
        'dashboard__right--no-info': !this.displayInformations,
      };
    },
    /** Boolean: Is the device the width of a smartphone landscape */
    isLandscape() {
      return this.$store.state.device.isSmartphoneLandscape;
    },
    rightSectionsHeight() {
      if (this.isLandscape) {
        return {};
      }
      return { height: this.displayInformations ? '50%' : '100%' };
    },
    displayInformations() {
      return this.currentTeam.dbDisplayInfos === 1;
    },
    /** Array: Next events */
    nextEvents() {
      return [...this.nextMeetings, ...this.nextGWs]
        .sort((evt1, evt2) => this.$moment(evt1.date).isBefore(evt2.date) ? 1 : -1);
    },
    /** Filtered upcoming meeting occurrences */
    nextMeetings() {
      const meetings = cloneDeep(this.$store.state[GET_NEXT_MEETINGS.stateKey] || []);
      const now = this.$moment();
      return meetings.filter(meeting => this.$moment.duration(this.$moment(meeting.date).diff(now)).asHours() < 12);
    },
    /** Filtered upcoming GW occurrences */
    nextGWs() {
      const gws = cloneDeep(this.$store.state[GET_NEXT_GWS.stateKey] || []);
      const now = this.$moment();
      return gws.filter(gw => this.$moment.duration(this.$moment(gw.date).diff(now)).asHours() < 12);
    },
    ...mapState({
      currentTeam: state => state.currentTeam,
      users: state => state[GET_USERS.stateKey] || [],
      usersLoading: state => state[GET_USERS.loadingKey],
    }),
    /** Array: IA buttons */
    iaAnalysisBtns() {
      // TODO add buttons from actions and problems
      if (this.$refs.indicators) {
        return [...this.$refs.indicators.iaAnalysisBtns, ...this.iaInitialBtns.slice()];
      }
      return this.iaInitialBtns.slice();
    },
  },

  created() {
    // Update teams and their users for NextMeetingPanel components
    this.$store.dispatch(FETCH_TEAMS).then(() => {
      this.$nextTick(() => { this.nextMeetingKeyModifier++; });
    });
    this.refresh();
    if (this.currentTeam.accessGW) {
      this.iaTeachBtns.push(this.teacherBtn('GW'));
    }
  },

  beforeDestroy() {
    clearTimeout(this.meetingsRefreshTask);
  },

  methods: {
    refresh() {
      if (this.demoMode) {
        return;
      }
      this.refreshNextMeetings();
      if (!this.usersLoading) {
        this.$store.dispatch(FETCH_USERS);
      }
    },
    /** Fetch the next meetings from now to display it at the top right corner */
    refreshNextMeetings() {
      this.$store.dispatch(FETCH_NEXT_MEETINGS);
      this.$store.dispatch(FETCH_NEXT_GWS);
      // Refresh in 3 hours
      this.meetingsRefreshTask = setTimeout(this.refreshNextMeetings, 10800000);
    },
    /** Working days are loaded */
    userLoaded() {
      // GW sensei lesson
      if (this.currentTeam.accessGW) {
        this.iaTeachBtns.push(this.teacherBtn('GW'));
      }
    },
    createHelpStepsGroups() {
      return [
        // Indicators
        new IAHelpStepsGroup(
          this.$t('message.INDICATORS'),
          [
            new IAHelpStep(null, this.$t('help.DB_INTRO')),
            ...this.$refs.indicators.createHelpSteps().filter((i, idx) => idx > 0),
          ],
        ),
        // Actions
        new IAHelpStepsGroup(
          this.$t('message.ACTIONS'),
          [
            new IAHelpStep('.dashboard__left__actions', this.$t('help.DB_ACTIONS')),
            ...this.$refs.actions.createHelpSteps().filter((i, idx) => idx > 0),
          ],
        ),
        // Informations
        new IAHelpStepsGroup(
          this.$t('message.INFORMATIONS'),
          [
            new IAHelpStep('.dashboard__right__infos', this.$t('help.DB_INFORMATIONS')),
          ],
        ),
        // Problems
        new IAHelpStepsGroup(
          this.$t('message.PROBLEMS'),
          [
            new IAHelpStep('.dashboard__right__problems', this.$t('help.DB_PROBLEMS')),
            ...this.$refs.problems.createHelpSteps().filter((i, idx) => idx > 0),
          ],
        ),
      ];
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.dashboard {
  overflow: hidden;
  position: relative;

  &__content {
    display: flex;
    padding: $page-content-padding;
    box-sizing: border-box;
    height: 100%;
  }

  &__left {
    height: 100%;
    flex: 0 0 75%;
    display: flex;
    flex-flow: column;
    padding-right: $page-content-padding;
    box-sizing: border-box;
    min-width: 0px;

    &__indicators {
      flex: 1;
      min-height: 0px;
      margin-bottom: $page-content-padding;

      &.f-h {
        height: auto;
      }

      .indic-theme {
        h2 {
          margin: 0.5rem;
        }

        .indicators-wrapper {
          height: calc(100% - calc(1rem + #{$h2-line-height}));
        }

        .indicator--doughnut .indicator__main-wrapper {
          padding-top: 0.2rem;
        }

        .wrong-period-message {
          .wrong-period-content {
            .field-hint {
              line-height: 1.3rem;
              padding: 0px 5%;
              height: 2.6rem;
            }
          }
        }
      }
    }

    &__actions {
      flex: 0 0 250px;
      height: 250px;

      &.f-h {
        height: auto;
      }

      .filters-zone__content {
        max-height: 170px;
      }
    }
  }

  &__right {
    height: 100%;
    flex: 0 0 25%;
    display: flex;
    flex-flow: column;
    min-width: 0px;

    &__next-events {
      flex: 0 0 auto;
      min-height: 0;
      margin-bottom: $page-content-padding;
      padding: $page-content-padding;

      .upcoming-event {

        &:not(:first-child) {
          margin-top: .5rem;
        }
      }
    }

    &__pb-infos {
      display: flex;
      flex-flow: column;
      flex: 1;
      min-height: 0;
    }

    &__infos {
      flex: 0 0 calc(50% - 1rem);
      box-sizing: border-box;
      margin-bottom: $page-content-padding;
      min-height: 0px;

      .meeting-infos__new {
        margin-bottom: 1rem;
      }
    }

    &__problems {
      flex: 1;
      min-height: 0px;
      box-sizing: border-box;
    }
  }
}

@media (max-width: $tablet-landscape) {
  .dashboard {

    &__left {
      flex: 0 0 65%;

      &__indicators {
        .presets-wrapper {
          display: none;
        }
      }

      &__actions {
        .filters-zone__content {
          width: auto;
        }
      }
    }

    &__right {
      flex: 0 0 35%;
    }
  }
}

@media (max-width: $smartphone-landscape) {
  .dashboard {
    overflow: auto;

    &__content {
      flex-flow: column;
    }

    &__left {
      height: auto;
      flex: auto;
      padding-right: 0;

      &__indicators {
        flex: auto;
      }

      &__actions {
        height: auto;
        flex: auto;

        .action-plans__plans {
          width: 100%;
        }
      }
    }

    &__right {
      height: auto;
      flex: auto;
      display: block;
      min-height: 100%;

      &__infos {
        height: auto;
        flex: auto;
        margin-top: 1rem;

        .meeting-infos.f-h {
          height: auto;
        }
      }

      &__problems {
        flex: auto;
        height: 50%;
      }
    }
  }
}

@media (max-width: $small-smartphone-portrait) {
  .dashboard {
    &__left__indicators {
      .chart-period-selector {
        display: none;
      }
    }

    .plan-action-item {
      margin-right: 0px;
      max-width: calc(100% - 0.5rem);
    }
  }
}

@media (max-height: 500px) {
  .dashboard {
    &__right {
      &__problems {
        height: 100%;
      }
    }
  }
}
</style>
